import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, DollarSign, Building2, CreditCard, TrendingUp,
  Loader2, ChevronLeft, ChevronRight, Shield, AlertCircle
} from "lucide-react";
import axios from "axios";
import { useAdminAuth, getAdminAuthHeaders } from "@/hooks/useAdminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MerchantTransactions() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [creds, setCreds] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderTotal, setOrderTotal] = useState(0);
  const [orderPage, setOrderPage] = useState(1);

  useAdminAuth();

  const fetchSummary = async () => {
    try {
      const [sumRes, credRes] = await Promise.all([
        axios.get(`${API}/admin/merchant/summary`, { headers: getAdminAuthHeaders() }),
        axios.get(`${API}/admin/merchant/credentials`, { headers: getAdminAuthHeaders() })
      ]);
      setSummary(sumRes.data.summaries || []);
      setCreds(credRes.data.credentials || []);
    } catch { toast.error("Failed to load data"); }
    setLoading(false);
  };

  const fetchTransactions = async (email = null, p = 1) => {
    try {
      const params = { page: p, limit: 25 };
      if (email) params.customer_email = email;
      const res = await axios.get(`${API}/admin/merchant/transactions`, {
        headers: getAdminAuthHeaders(), params
      });
      setTransactions(res.data.transactions || []);
      setTotalCount(res.data.total || 0);
      setPage(p);
    } catch { toast.error("Failed to load transactions"); }
  };

  const fetchOrders = async (email = null, p = 1) => {
    try {
      const params = { page: p, limit: 25 };
      if (email) params.customer_email = email;
      const res = await axios.get(`${API}/admin/orders`, {
        headers: getAdminAuthHeaders(), params
      });
      setOrders(res.data.orders || []);
      setOrderTotal(res.data.total || 0);
      setOrderPage(p);
    } catch { console.error("Failed to load orders"); }
  };

  useEffect(() => { fetchSummary(); fetchTransactions(); fetchOrders(); }, []);

  const totalOutstanding = summary.reduce((s, v) => s + (v.outstanding || 0), 0);
  const totalFees = summary.reduce((s, v) => s + (v.total_platform_fees || 0), 0);
  const totalBookings = summary.reduce((s, v) => s + (v.total_bookings || 0), 0);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4 mr-1" /> Admin
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <DollarSign className="w-5 h-5" /> Merchant Transactions & 2% Fees
            </h1>
            <p className="text-sm text-gray-500">Track booking engine transactions and outstanding platform fees</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card><CardContent className="pt-5">
            <p className="text-xs font-medium text-gray-500">Total Bookings</p>
            <p className="text-2xl font-bold text-gray-900" data-testid="total-bookings">{totalBookings}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-5">
            <p className="text-xs font-medium text-gray-500">Total 2% Fees Earned</p>
            <p className="text-2xl font-bold text-emerald-600" data-testid="total-fees">${totalFees.toFixed(2)}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-5">
            <p className="text-xs font-medium text-gray-500">Outstanding (Unpaid)</p>
            <p className="text-2xl font-bold text-amber-600" data-testid="total-outstanding">${totalOutstanding.toFixed(2)}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-5">
            <p className="text-xs font-medium text-gray-500">Properties with Processors</p>
            <p className="text-2xl font-bold text-gray-900" data-testid="total-processors">{creds.length}</p>
          </CardContent></Card>
        </div>

        {/* Property Fee Summary */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Property Fee Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            {summary.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-6">No booking engine transactions yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 text-gray-600 font-medium">Property</th>
                      <th className="text-left py-2 px-3 text-gray-600 font-medium">Email</th>
                      <th className="text-right py-2 px-3 text-gray-600 font-medium">Bookings</th>
                      <th className="text-right py-2 px-3 text-gray-600 font-medium">Revenue</th>
                      <th className="text-right py-2 px-3 text-gray-600 font-medium">2% Fees</th>
                      <th className="text-right py-2 px-3 text-gray-600 font-medium">Paid</th>
                      <th className="text-right py-2 px-3 text-gray-600 font-medium">Outstanding</th>
                      <th className="text-center py-2 px-3 text-gray-600 font-medium">Processor</th>
                      <th className="text-center py-2 px-3 text-gray-600 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.map((s, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`summary-row-${i}`}>
                        <td className="py-2.5 px-3 font-medium">{s.property_name || "—"}</td>
                        <td className="py-2.5 px-3 text-gray-500 text-xs">{s.customer_email}</td>
                        <td className="py-2.5 px-3 text-right">{s.total_bookings}</td>
                        <td className="py-2.5 px-3 text-right font-medium">${(s.total_booking_revenue || 0).toFixed(2)}</td>
                        <td className="py-2.5 px-3 text-right text-emerald-600 font-medium">${(s.total_platform_fees || 0).toFixed(2)}</td>
                        <td className="py-2.5 px-3 text-right text-gray-500">${(s.total_paid || 0).toFixed(2)}</td>
                        <td className="py-2.5 px-3 text-right">
                          <span className={`font-bold ${s.outstanding > 0 ? "text-amber-600" : "text-gray-400"}`}>
                            ${(s.outstanding || 0).toFixed(2)}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          {s.payment_processor ? (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs capitalize">{s.payment_processor}</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs text-gray-400">None</Badge>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <Button size="sm" variant="outline" className="text-xs h-7"
                            onClick={() => { setSelectedEmail(s.customer_email); fetchTransactions(s.customer_email, 1); }}
                            data-testid={`view-txns-${i}`}>
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Merchant Credentials Overview */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <Shield className="w-4 h-4" /> Merchant Credentials Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {creds.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">No merchant credentials configured by any property</p>
            ) : (
              <div className="space-y-2">
                {creds.map((c, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`cred-row-${i}`}>
                    <div className="flex items-center gap-3">
                      <Badge className="capitalize bg-slate-200 text-slate-700">{c.processor}</Badge>
                      <span className="text-sm font-medium">{c.customer_email}</span>
                      <Badge variant="outline" className="text-xs">{c.environment}</Badge>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      {c.masked_credentials && Object.entries(c.masked_credentials).slice(0, 2).map(([k, v]) => (
                        <span key={k}>{k}: <code className="bg-white px-1 rounded">{v}</code></span>
                      ))}
                      <Badge className={c.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{c.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Full Orders with Payment Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> Orders & Payment Details
              <span className="text-gray-400 font-normal">({orderTotal})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {orders.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-6">No orders yet</p>
            ) : (
              <div className="space-y-3">
                {orders.map((o, i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-4" data-testid={`order-${i}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className="font-bold text-sm">{o.guest_name}</span>
                        <span className="text-xs text-gray-400 ml-2">{o.guest_email}</span>
                        <p className="text-xs text-gray-500 mt-0.5">{o.property_name}</p>
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-lg">${(o.booking_total || 0).toFixed(2)}</span>
                        <p className="text-xs text-emerald-600 font-medium">2% fee: ${(o.platform_fee_amount || 0).toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-3 text-xs mb-3">
                      <div><span className="text-gray-400 block">Check-in</span><span className="font-medium">{o.checkin_date}</span></div>
                      <div><span className="text-gray-400 block">Check-out</span><span className="font-medium">{o.checkout_date}</span></div>
                      <div><span className="text-gray-400 block">Nights</span><span className="font-medium">{o.nights || "—"}</span></div>
                      <div><span className="text-gray-400 block">Booked</span><span className="font-medium">{new Date(o.created_at).toLocaleDateString()}</span></div>
                    </div>
                    {/* Payment Details */}
                    {o.payment ? (
                      <div className="bg-blue-50 rounded-lg p-3 mt-2">
                        <div className="flex items-center gap-2 mb-2">
                          <CreditCard className="w-3.5 h-3.5 text-blue-600" />
                          <span className="text-xs font-bold text-blue-800">Payment Details</span>
                          <Badge className={o.payment.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"} variant="outline">{o.payment.status}</Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div><span className="text-gray-500 block">Card</span><span className="font-bold">{o.payment.card_brand} ****{o.payment.card_last4}</span></div>
                          <div><span className="text-gray-500 block">Approval</span><span className="font-mono font-bold">{o.payment.approval_code || "—"}</span></div>
                          <div><span className="text-gray-500 block">Order ID</span><span className="font-mono text-[10px]">{o.payment.order_id || "—"}</span></div>
                          <div><span className="text-gray-500 block">Transaction ID</span><span className="font-mono text-[10px]">{o.payment.ipg_transaction_id || "—"}</span></div>
                          <div><span className="text-gray-500 block">Reference</span><span className="font-mono text-[10px]">{o.payment.ref_number || "—"}</span></div>
                          <div><span className="text-gray-500 block">Processor</span><span className="font-medium capitalize">{o.payment.processor || "—"}</span></div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-amber-50 rounded-lg p-2 mt-2 text-xs text-amber-700">No payment transaction recorded</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Transaction Detail */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-bold flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                {selectedEmail ? `Transactions: ${selectedEmail}` : "All Booking Engine Transactions"}
                <span className="text-gray-400 font-normal">({totalCount})</span>
              </CardTitle>
              {selectedEmail && (
                <Button size="sm" variant="ghost" onClick={() => { setSelectedEmail(null); fetchTransactions(null, 1); }} data-testid="clear-filter">
                  Clear filter
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-6">No transactions found</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-2 text-gray-600 font-medium text-xs">Date</th>
                        <th className="text-left py-2 px-2 text-gray-600 font-medium text-xs">Guest</th>
                        <th className="text-left py-2 px-2 text-gray-600 font-medium text-xs">Property</th>
                        <th className="text-left py-2 px-2 text-gray-600 font-medium text-xs">Check-in</th>
                        <th className="text-right py-2 px-2 text-gray-600 font-medium text-xs">Booking Total</th>
                        <th className="text-right py-2 px-2 text-gray-600 font-medium text-xs">2% Fee</th>
                        <th className="text-center py-2 px-2 text-gray-600 font-medium text-xs">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((t, i) => (
                        <tr key={i} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`txn-row-${i}`}>
                          <td className="py-2 px-2 text-xs text-gray-500">{new Date(t.created_at).toLocaleDateString()}</td>
                          <td className="py-2 px-2 text-xs font-medium">{t.guest_name}</td>
                          <td className="py-2 px-2 text-xs">{t.property_name}</td>
                          <td className="py-2 px-2 text-xs">{t.checkin_date}</td>
                          <td className="py-2 px-2 text-right text-xs font-medium">${(t.booking_total || 0).toFixed(2)}</td>
                          <td className="py-2 px-2 text-right text-xs font-bold text-emerald-600">${(t.platform_fee_amount || 0).toFixed(2)}</td>
                          <td className="py-2 px-2 text-center">
                            <Badge className={t.status === "confirmed" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"} variant="outline">
                              {t.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                <div className="flex items-center justify-between mt-4">
                  <span className="text-xs text-gray-400">Page {page} of {Math.max(1, Math.ceil(totalCount / 25))}</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" disabled={page <= 1}
                      onClick={() => fetchTransactions(selectedEmail, page - 1)} data-testid="prev-page">
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" disabled={page >= Math.ceil(totalCount / 25)}
                      onClick={() => fetchTransactions(selectedEmail, page + 1)} data-testid="next-page">
                      <ChevronRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
